/**
 * Tool Input Schemas - Single Source of Truth
 *
 * PROMPT ENGINEERING BEST PRACTICES APPLIED:
 * 1. Use SimpleDateString (simple YYYY-MM-DD regex) for date validation
 *    NOTE: z.string().date() generates complex leap-year patterns that confuse some models
 * 2. Clear, descriptive .describe() on all properties with examples
 * 3. Semantically meaningful parameter names
 * 4. Minimal nesting and complexity
 * 5. Strict mode enabled on tools for better LLM compatibility
 *
 * NOTE: Descriptions are used by the AI to understand how to call tools.
 */

import { z } from "zod";
import {
	PersonNameStrictOptionalSchema,
	PersonNameStrictSchema,
} from "../common/name";
import { PhoneE164Schema, PhoneE164StrictSchema } from "../common/phone";
import { LocalDateTimeStringSchema } from "../datetime";

/**
 * Reject anything that isn't a read-only single SELECT (or CTE) statement.
 *
 * Defense-in-depth atop the `rag_readonly` RLS role: the value must start with
 * WITH/SELECT, contain no data-modifying keywords, and be a single statement
 * (at most one trailing semicolon). A `.refine` predicate does NOT serialize
 * into JSON Schema, so it stays byte-identical across the two schema copies.
 */
const READ_ONLY_SQL_START = /^\s*(WITH|SELECT)\b/i;
const FORBIDDEN_SQL_KEYWORD =
	/\b(INSERT|UPDATE|DELETE|DROP|ALTER|CREATE|TRUNCATE|GRANT|REVOKE|COPY)\b/i;
const TRAILING_SEMICOLON = /;\s*$/;
const isReadOnlySelectSql = (sql: string): boolean => {
	if (!READ_ONLY_SQL_START.test(sql)) {
		return false;
	}
	// Allow a single trailing semicolon; reject any interior statement terminator.
	if (sql.trim().replace(TRAILING_SEMICOLON, "").includes(";")) {
		return false;
	}
	return !FORBIDDEN_SQL_KEYWORD.test(sql);
};
const READ_ONLY_SQL_MESSAGE =
	"Use one read-only SELECT/WITH; no writes, DDL, COPY, or multiple statements.";

// ============================================================================
// Shared Schema Components
// ============================================================================

/**
 * Simple date string pattern (YYYY-MM-DD).
 * Uses a simple regex instead of z.string().date() which generates complex leap-year patterns
 * that may confuse some AI models.
 */
const SimpleDateString = z
	.string()
	.regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format");

/**
 * Date range with optional weekday filter.
 * Used across getEventSnapshots, batchModify, batchCancel for consistent date filtering.
 */
export const DateRangeSchema = z.object({
	start: SimpleDateString.describe(
		'Start date in YYYY-MM-DD format. Example: "2026-01-20"'
	),
	end: SimpleDateString.optional().describe(
		'End date in YYYY-MM-DD format. Omit for single day query. Example: "2026-01-26"'
	),
	weekdays: z
		.array(z.number().int().min(0).max(6))
		.optional()
		.describe("Weekday filter: 0=Sun through 6=Sat. Example: [1,3,5]."),
});

// ============================================================================
// Shared Tools (assistant + system-agent)
// ============================================================================

export const getCurrentDatetimeInputSchema = z.object({});

// ============================================================================
// Assistant Tools
// ============================================================================

export const sendBusinessLocationInputSchema = z.object({});

export const handoffToHumanInputSchema = z.object({
	reason: z
		.string()
		.min(1)
		.optional()
		.describe(
			'Brief handoff reason shown to the operator, e.g. "user requested a human".'
		),
});

export const saveFeedbackInputSchema = z.object({
	quote: z
		.string()
		.min(1)
		.describe(
			"Customer's exact words; do not paraphrase, translate, or correct."
		),
	summary: z
		.string()
		.min(1)
		.describe(
			"One-sentence feedback paraphrase in the operator's configured language; plain text."
		),
	sentiment: z
		.enum(["positive", "negative", "neutral"])
		.describe(
			"positive=praise; negative=complaint/frustration/incident; neutral=mixed/factual."
		),
});

export const getUserDataInputSchema = z.object({
	include_past: z
		.boolean()
		.optional()
		.describe(
			"Include past events in addition to upcoming ones. Default: false"
		),
});

export const getAvailableTimeSlotsInputSchema = z.object({
	event_type: z
		.string()
		.min(1)
		.optional()
		.describe(
			"Event type to evaluate. Omit only when the organization default is intended."
		),
	date: SimpleDateString.describe(
		"Date as YYYY-MM-DD; use a Hijri date when hijri=true."
	),
	hijri: z
		.boolean()
		.optional()
		.describe(
			"If true, interpret date as Hijri calendar. Default: false (Gregorian)"
		),
});

export const searchAvailableAppointmentsInputSchema = z.object({
	event_type: z
		.string()
		.min(1)
		.optional()
		.describe(
			"Event type to evaluate. Different durations, buffers, notice, and capacity can produce different slots."
		),
	startDate: SimpleDateString.optional().describe(
		'Date to start searching from in YYYY-MM-DD format. Default: today. Example: "2026-01-20"'
	),
	timeSlot: z
		.string()
		.optional()
		.describe("Optional preferred time in 12h/24h format; omit for all slots."),
	daysForward: z
		.number()
		.int()
		.min(1)
		.max(30)
		.optional()
		.describe(
			"Number of days to search forward from startDate. Default: 3. Example: 7"
		),
	daysBackward: z
		.number()
		.int()
		.min(0)
		.max(30)
		.optional()
		.describe(
			"Number of days to search backward from startDate. Default: 0. Example: 2"
		),
	hijri: z
		.boolean()
		.optional()
		.describe(
			"If true, interpret startDate as Hijri calendar. Default: false (Gregorian)"
		),
});

// The [contact profile] card renders beneficiaries as `name #id`; models often
// copy the leading hash into beneficiary_id — strip it before lookup.
const LEADING_HASH_REGEX = /^#/;

/**
 * Filler vocabulary models reach for when a field has no legal "not applicable"
 * value. Matched AFTER trimming and hash-stripping, so `.`, `-`, `N/A`, `null`,
 * and the schema's own type name all normalize to "omitted".
 *
 * Three families, all observed verbatim in production:
 *  - punctuation / type names / null-ish words — the original 2026-07-25 set;
 *  - SELF-REFERENCE (`self`, `SELF_BOOKING`, `contact`, `me`, `owner`, …) — the
 *    model naming the semantic it wants ("book for the contact") in a field that
 *    has no vocabulary for it. 100 of the 126 INVALID_BENEFICIARY failures in
 *    the two weeks to 2026-07-28 were this family;
 *  - degenerate ids (`0`, `1`, `x`, the all-zero UUID) — a "null id" idiom.
 *
 * Safe to absorb wholesale: a real beneficiary id is a `cuid()` (25 lowercase
 * alphanumerics starting with `c`), so nothing here can collide with one. Any
 * NON-filler value that still fails to resolve is self-healed at the call site
 * rather than guessed at here — see the explicit-reuse arm of `scheduleEvent`.
 */
const PLACEHOLDER_ID_REGEX =
	/^(?:[.\-_/\\]+|n\/?a|none|null|nil|nan|undefined|unknown|empty|string|id|self|me|myself|mine|owner|user|contact|customer|patient|caller|self[\s_-]*booking|no[\s_-]*beneficiary|not[\s_-]*applicable|x+|\d+|[0-]+)$/i;

/**
 * Optional beneficiary id for AI-tool inputs. Mirrors
 * `PersonNameStrictOptionalSchema`: `.nullish()` so the model-facing JSON Schema
 * carries a `null` branch, plus placeholder normalization BEFORE validation.
 *
 * Without both halves the model has no legal way to express "self-booking" — it
 * cannot omit a property it habitually fills, cannot send `null` (absent from
 * the type), and cannot send `""` (`minLength: 1`) — so it emits the shortest
 * legal filler, `"."`. That reached the repository as a contact id and hard
 * failed with INVALID_BENEFICIARY, losing real bookings in production
 * (2026-07-25). A normalized placeholder now books for the contact themselves,
 * and `booked_for` in the result tells the model who it actually booked.
 */
const OptionalBeneficiaryIdSchema = z
	.string()
	.nullish()
	.transform((value) => {
		const trimmed = value?.replace(LEADING_HASH_REGEX, "").trim();
		return trimmed && !PLACEHOLDER_ID_REGEX.test(trimmed) ? trimmed : undefined;
	})
	.pipe(z.string().min(1).optional());

export const scheduleEventInputSchema = z.object({
	user_name: PersonNameStrictOptionalSchema.describe(
		"The messaging contact's OWN full name — never the beneficiary's. Omit when the contact profile already shows a saved name (the saved name is used automatically). Optional for Messenger users without one."
	),
	eventDateTime: LocalDateTimeStringSchema.describe(
		'Event datetime in LOCAL ISO format without timezone. Example: "2026-01-20T10:00:00"'
	),
	endsAt: LocalDateTimeStringSchema.nullish().describe(
		'Checkout of a MULTI-NIGHT stay — the exclusive end of the interval, in LOCAL ISO without timezone. ONLY for event types listed as "up to N nights"; omit or send null for every other type and for the shortest stay, whose end the type\'s own length already fixes. Example: check in "2026-01-20T15:00:00", check out "2026-01-23T12:00:00" = 3 nights.'
	),
	event_type: z
		.string()
		.min(1)
		.describe('Allowed event type ID, e.g. "CHECK_UP".'),
	beneficiary_id: OptionalBeneficiaryIdSchema.describe(
		'ID of an EXISTING beneficiary, taken from the [contact profile] card or the getUserData beneficiaries list. ALWAYS prefer this over beneficiary_name when the person is already listed; takes precedence when both are set. Booking for the contact themselves: omit or send null — never a placeholder like ".", "-", "N/A", "self", "0", or the contact\'s own name.'
	),
	beneficiary_name: PersonNameStrictOptionalSchema.describe(
		"Full name of a NEW beneficiary (a phoneless other person the contact books for) — ONLY when that person is NOT in the beneficiaries list; spelling/spacing variations of a listed name mean use beneficiary_id instead. The booking stays under the messaging contact. Booking for the contact themselves: omit or send null — never the contact's own name, blanks, or placeholders."
	),
});

const waitlistDesiredWhenSchema = z.discriminatedUnion("kind", [
	z.object({
		kind: z.literal("slot"),
		slotStart: LocalDateTimeStringSchema.describe(
			"Desired slot in local time."
		),
	}),
	z.object({
		kind: z.literal("window"),
		dateStart: SimpleDateString,
		dateEnd: SimpleDateString,
		timeStart: z
			.string()
			.regex(/^([01]\d|2[0-3]):[0-5]\d$/)
			.optional(),
		timeEnd: z
			.string()
			.regex(/^([01]\d|2[0-3]):[0-5]\d$/)
			.optional(),
		weekdays: z.array(z.number().int().min(0).max(6)).max(7).optional(),
	}),
]);

export const joinWaitlistInputSchema = z.object({
	event_type: z
		.string()
		.min(1)
		.describe("Event type ID requested by the user."),
	desired_when: waitlistDesiredWhenSchema,
	user_name: PersonNameStrictOptionalSchema,
});

export const modifyEventInputSchema = z.object({
	event_id_to_modify: z
		.number()
		.int()
		.min(1)
		.describe(
			"ID of the event to modify. Call getUserData first to find the correct ID. Example: 123"
		),
	newEventDateTime: LocalDateTimeStringSchema.optional().describe(
		"New local ISO datetime without timezone; omit to keep current."
	),
	newEndsAt: LocalDateTimeStringSchema.nullish().describe(
		'New checkout of a MULTI-NIGHT stay — the exclusive end, in local ISO without timezone. ONLY for event types listed as "up to N nights"; omit or send null to keep the current one. Send it together with newEventDateTime when the whole stay moves.'
	),
	new_name: PersonNameStrictOptionalSchema.describe(
		'New full name (first and last name required). Omit to keep current name. Example: "Jane Doe"'
	),
	new_type: z
		.string()
		.min(1)
		.optional()
		.describe("New allowed event type ID; omit to keep current."),
});

export const cancelEventInputSchema = z.object({
	event_id_to_cancel: z
		.number()
		.int()
		.min(1)
		.describe(
			"ID of the event to cancel. Call getUserData first to find the correct ID. Example: 123"
		),
	reason: z
		.string()
		.min(1)
		.optional()
		.describe(
			"Optional cancellation reason shown in notifications and audit logs."
		),
});

export const requestPhoneNumberInputSchema = z
	.object({
		message: z.string().trim().min(1).max(4096).optional(),
		buttonText: z.string().trim().min(1).max(64).optional(),
	})
	.strict();

export const sendInteractiveButtonsInputSchema = z.object({
	body: z.object({
		text: z
			.string()
			.min(1)
			.max(1024)
			.describe("Message text displayed above buttons. Max 1024 characters."),
	}),
	buttons: z
		.array(
			z.object({
				id: z
					.string()
					.min(1)
					.max(256)
					.describe(
						'Unique button ID returned when user clicks. Example: "confirm_yes"'
					),
				title: z
					.string()
					.min(1)
					.max(20)
					.describe(
						'Button label shown to user. Max 20 characters. Example: "Yes"'
					),
			})
		)
		.min(1)
		.max(3)
		.describe("Array of 1-3 buttons. WhatsApp allows maximum 3 buttons."),
	header: z
		.object({
			type: z.literal("text"),
			text: z
				.string()
				.min(1)
				.max(60)
				.describe("Header text. Max 60 characters."),
		})
		.optional()
		.describe("Optional header above the message body."),
	footer: z
		.object({
			text: z
				.string()
				.min(1)
				.max(60)
				.describe("Footer text. Max 60 characters."),
		})
		.optional()
		.describe("Optional footer below the buttons."),
});

export const sendInteractiveListInputSchema = z.object({
	body: z.object({
		text: z
			.string()
			.min(1)
			.max(1024)
			.describe(
				"Message text displayed above the list button. Max 1024 characters."
			),
	}),
	buttonText: z
		.string()
		.min(1)
		.max(20)
		.describe(
			'Text on the button that opens the list menu. Max 20 characters. Example: "Select Option"'
		),
	sections: z
		.array(
			z.object({
				title: z
					.string()
					.max(24)
					.optional()
					.describe("Section title. Max 24 characters."),
				rows: z
					.array(
						z.object({
							id: z
								.string()
								.min(1)
								.max(200)
								.describe(
									'Unique row ID returned when selected. Example: "option_1"'
								),
							title: z
								.string()
								.min(1)
								.max(24)
								.describe("Row title shown to user. Max 24 characters."),
							description: z
								.string()
								.max(72)
								.optional()
								.describe("Row description. Max 72 characters."),
						})
					)
					.min(1)
					.max(10)
					.describe("Rows in this section. 1-10 rows per section."),
			})
		)
		.min(1)
		.max(10)
		.describe(
			"List sections. 1-10 sections, max 10 total rows across all sections."
		),
	header: z
		.object({
			type: z.literal("text"),
			text: z
				.string()
				.min(1)
				.max(60)
				.describe("Header text. Max 60 characters."),
		})
		.optional()
		.describe("Optional header above the message."),
	footer: z
		.object({
			text: z
				.string()
				.min(1)
				.max(60)
				.describe("Footer text. Max 60 characters."),
		})
		.optional()
		.describe("Optional footer below the list."),
});

// ============================================================================
// System-Agent Tools
// ============================================================================

/**
 * Get Event Snapshots - Query events across date ranges
 *
 * OUTPUT: Returns array of events with id, datetime, type, status, user info.
 * Use the event IDs from output for batchModify or batchCancel operations.
 */
export const getEventSnapshotsInputSchema = z.object({
	ranges: z
		.array(DateRangeSchema)
		.min(1)
		.describe("Required date ranges; omit end for one day."),
	phones: z
		.array(PhoneE164Schema)
		.optional()
		.describe(
			'Filter by phone numbers in E.164 format. Example: ["+1234567890"]'
		),
	search: z
		.string()
		.optional()
		.describe('Search by user name (partial match). Example: "John"'),
	types: z
		.array(z.string().min(1))
		.optional()
		.describe("Filter by allowed event type IDs."),
	include_cancelled: z
		.boolean()
		.optional()
		.describe("Include cancelled events in results. Default: false"),
	limit: z
		.number()
		.int()
		.min(1)
		.max(500)
		.optional()
		.describe("Maximum number of results. Default: 100. Example: 50"),
});

/**
 * Get Available Slots Batch - Check availability for multiple dates
 *
 * OUTPUT: Returns available time slots for each requested date.
 */
export const getAvailableSlotsBatchInputSchema = z.object({
	dates: z
		.array(SimpleDateString)
		.min(1)
		.max(30)
		.describe("Required YYYY-MM-DD dates."),
	hijri: z
		.boolean()
		.optional()
		.describe(
			"If true, interpret dates as Hijri calendar. Default: false (Gregorian)"
		),
});

/**
 * Batch Schedule Events - Schedule multiple events at once
 *
 * OUTPUT: Returns success/failure status for each scheduled event.
 */
const batchScheduleEventSchema = z.object({
	eventDateTime: LocalDateTimeStringSchema.describe(
		'Event datetime in LOCAL ISO format. Example: "2026-01-20T10:00:00"'
	),
	endsAt: LocalDateTimeStringSchema.nullish().describe(
		'Checkout of a MULTI-NIGHT stay — the exclusive end of the interval, in LOCAL ISO without timezone. ONLY for event types listed as "up to N nights"; omit or send null for every other type and for the shortest stay, whose end the type\'s own length already fixes. Example: check in "2026-01-20T15:00:00", check out "2026-01-23T12:00:00" = 3 nights.'
	),
	event_type: z.string().min(1).describe("Allowed event type ID."),
});

const batchScheduleRequestItemSchema = z.object({
	phone_e164: PhoneE164StrictSchema.describe(
		'User phone number in E.164 format. Example: "+1234567890"'
	),
	user_name: PersonNameStrictSchema.describe(
		'Full name (first and last name required). Example: "John Smith"'
	),
	events: z
		.array(batchScheduleEventSchema)
		.min(1)
		.max(10)
		.describe("Events to schedule for this user. 1-10 events per user."),
});

export const scheduleEventsInputSchema = z.object({
	requests: z
		.array(batchScheduleRequestItemSchema)
		.min(1)
		.max(50)
		.describe(
			"REQUIRED: Schedule requests grouped by user. 1-50 users per batch."
		),
	verbosity: z
		.enum(["summary", "detailed"])
		.optional()
		.describe(
			'Response detail level. "summary" = counts only, "detailed" = full results. Default: "summary"'
		),
});

/**
 * Modify Events - Modify specific events by ID
 * Use this when you know the exact event IDs to modify.
 */
export const modifyEventsInputSchema = z.object({
	events: z
		.array(
			z.object({
				id: z.number().int().min(1).describe("Event ID"),
				datetime: LocalDateTimeStringSchema.optional().describe(
					'New datetime. Example: "2026-01-21T14:00:00"'
				),
				endsAt: LocalDateTimeStringSchema.nullish().describe(
					"New checkout of a MULTI-NIGHT stay (extend or shorten it). Same rules as the checkout on scheduleEvents; omit or send null to keep the current one."
				),
				name: PersonNameStrictSchema.optional().describe(
					'New full name (first and last name required); omit to keep current. Example: "Jane Doe"'
				),
				type: z
					.string()
					.min(1)
					.optional()
					.describe("New allowed event type ID."),
			})
		)
		.min(1)
		.describe("Events to modify with their new values"),
});

/**
 * Modify Event Range - Bulk modify all events in a date range
 * Use this to shift or change type for multiple events at once.
 */
export const modifyEventRangeInputSchema = z.object({
	// Required: date range
	start: SimpleDateString.describe('Start date. Example: "2026-01-20"'),
	end: SimpleDateString.optional().describe("End date. Omit for single day."),
	// Optional filters
	weekdays: z
		.array(z.number().int().min(0).max(6))
		.optional()
		.describe("Filter weekdays: 0=Sun..6=Sat"),
	phones: z
		.array(PhoneE164Schema)
		.optional()
		.describe("Filter by phones in E.164 format"),
	types: z
		.array(z.string().min(1))
		.optional()
		.describe("Filter by event type IDs"),
	// Changes to apply (at least one required)
	shift_days: z
		.number()
		.int()
		.optional()
		.describe("Shift by N days; use multiples of 7 for weeks"),
	new_type: z
		.string()
		.min(1)
		.optional()
		.describe("Set every match to this allowed event type ID."),
});

/**
 * Cancel Events - Cancel specific events by ID
 * Use this when you know the exact event IDs to cancel.
 */
export const cancelEventsInputSchema = z.object({
	event_ids: z
		.array(z.number().int().min(1))
		.min(1)
		.describe("Event IDs to cancel"),
	reason: z
		.string()
		.min(1)
		.optional()
		.describe(
			"Optional cancellation reason shown in notifications and audit logs."
		),
});

/**
 * Cancel Event Range - Cancel all events in a date range
 * Use this to cancel multiple events matching a date range.
 */
export const cancelEventRangeInputSchema = z.object({
	start: SimpleDateString.describe('Start date. Example: "2026-01-20"'),
	end: SimpleDateString.optional().describe("End date. Omit for single day."),
	weekdays: z
		.array(z.number().int().min(0).max(6))
		.optional()
		.describe("Filter weekdays: 0=Sun..6=Sat"),
	phones: z
		.array(PhoneE164Schema)
		.optional()
		.describe("Filter by phones in E.164 format"),
	types: z
		.array(z.string().min(1))
		.optional()
		.describe("Filter by event type IDs"),
	reason: z
		.string()
		.min(1)
		.optional()
		.describe(
			"Optional cancellation reason shown in notifications and audit logs."
		),
});

// ============================================================================
// RAG Document Search Tool
// ============================================================================

/**
 * Search uploaded documents for relevant information.
 * Uses hybrid semantic + keyword search over document chunks.
 */
export const searchDocsInputSchema = z
	.object({
		query: z
			.string()
			.min(1)
			.max(500)
			.describe(
				'Primary search query. Use concise keywords, not full questions. Example: "refund policy terms"'
			),
		additionalQueries: z
			.array(z.string().min(1).max(500))
			.max(3)
			.optional()
			.describe(
				"Up to 3 parallel alternative queries; results are deduplicated."
			),
		documentId: z
			.number()
			.int()
			.optional()
			.describe(
				"Filter to a specific document by ID. Use getDocumentSchemas to find IDs."
			),
		limit: z
			.number()
			.int()
			.min(1)
			.max(20)
			.optional()
			.describe("Maximum hybrid search results. Default: 10"),
		metadata: z
			.record(z.string(), z.string())
			.optional()
			.describe(
				'Filter by metadata (JSONB containment). Example: {"category": "HR"}'
			),
		semanticWeight: z
			.number()
			.min(0)
			.max(1)
			.optional()
			.describe(
				"Weight for semantic/vector search. Higher for conceptual questions. Default: 0.5"
			),
		keywordWeight: z
			.number()
			.min(0)
			.max(1)
			.optional()
			.describe(
				"Weight for keyword/FTS search. Higher for exact phrase lookups. Default: 0.3"
			),
		patternWeight: z
			.number()
			.min(0)
			.max(1)
			.optional()
			.describe(
				"Weight for pattern/trigram matching. Higher for codes, IDs, part numbers. Default: 0.2"
			),
		fullText: z
			.boolean()
			.optional()
			.describe(
				"DEPRECATED — use the readSection tool with documentId instead. When true, returns the full extracted text of a single document (requires documentId). fullText=true only when the document is small (sizeChars ≲ 30k) or when outline-guided section reads have failed to assemble the answer; prefer getDocumentOutline + readSection for large documents."
			),
	})
	.describe(
		"Queries must be standalone; use entity names from context, never pronouns. Use `metadata` filters conservatively; if a filtered search returns zero hits, retry the same query WITHOUT the filter before concluding the corpus lacks the answer."
	);

/**
 * List available documents, their column schemas, and metadata categories.
 * Call before using queryStructuredData or metadata filters in searchDocs.
 */
export const getDocumentSchemasInputSchema = z.object({
	documentIds: z
		.array(z.number().int())
		.optional()
		.describe(
			"Scope to specific document IDs. Omit to list every document. Schemas already shown as preloaded in the system prompt need no lookup."
		),
	question: z
		.string()
		.optional()
		.describe(
			"The standalone, self-contained natural-language question you are trying to answer, with all conversational context resolved — no pronouns, no fragments (same rule as searchDocs queries). When set, the result may include `fewShotExamples`: proven past questions and the exact SQL that answered them, for you to adapt."
		),
});

/**
 * Execute a SQL query against structured tabular data (Excel/CSV).
 * The LLM writes PostgreSQL queries against the rag_structured_row table.
 * Data is stored in a JSONB column called "rowData".
 * Use rowData->>'ColumnName' to access columns.
 */
export const queryStructuredDataInputSchema = z.object({
	sql: z
		.string()
		.min(1)
		.max(2000)
		.refine(isReadOnlySelectSql, READ_ONLY_SQL_MESSAGE)
		.describe(
			"Read-only SELECT on rag_structured_row. Access columns as \"rowData\"->>'Name'; cast numbers with ::numeric. One statement, no trailing semicolon."
		),
	documentId: z
		.number()
		.int()
		.optional()
		.describe(
			"The document the query reads (IDs from getDocumentSchemas). Pass it whenever the rows come from one document, so the result cites that document."
		),
	question: z
		.string()
		.optional()
		.describe(
			"The standalone, self-contained natural-language question this SQL answers, with all conversational context resolved — no pronouns, no fragments (same rule as searchDocs queries). Recorded with the query on success so future paraphrases can reuse it."
		),
});

/**
 * Generate a structured summary of a customer's conversation history.
 *
 * Operator-only system-agent tool. The agent invokes this when the operator
 * asks for a summary, optionally pinpointing a contact via @-mention. The
 * server resolves the contact from `contactPhoneE164` (preferred) or
 * `contactId` and reads the conversation history itself — the agent does
 * NOT need to send transcript text.
 */
export const summarizeConversationInputSchema = z.object({
	contactPhoneE164: PhoneE164Schema.optional().describe(
		"Contact E.164 phone, preferred for @-mentions. Required unless contactId is set."
	),
	contactId: z
		.string()
		.optional()
		.describe(
			"Internal contact ID. Use only if you do not have the phone number."
		),
	channel: z
		.enum([
			"whatsapp",
			"sms",
			"email",
			"telegram",
			"messenger",
			"instagram",
			"x",
			"system_agent",
		])
		.optional()
		.describe("Summary channel; omit to combine all contact channels."),
	fromDate: SimpleDateString.optional().describe(
		"Optional inclusive start date (YYYY-MM-DD). Omit to summarize from the conversation start."
	),
	toDate: SimpleDateString.optional().describe(
		"Optional inclusive end date (YYYY-MM-DD). Omit to summarize up to the latest message."
	),
});

// ============================================================================
// collectLead — assistant-only tool with PER-ORG DYNAMIC INPUT SCHEMA.
// ============================================================================

/**
 * Static contract input schema for `collectLead`.
 *
 * The actual schema sent to the LLM is REBUILT PER-ORG at runtime by
 * `createCollectLeadTool` from the org's `CapturedLeadFieldDefinition` rows
 * (so each field becomes a strictly-typed property with the operator's
 * description). This permissive shape exists only so the contract registry
 * has a stable input type — the runtime schema replaces it via
 * `implementTool({ inputSchema })`.
 *
 * Stored as a record-of-primitives (not `passthrough`) so it survives strict
 * mode in case any path reaches the contract schema directly.
 */
export const collectLeadInputSchema = z
	.record(z.string(), z.union([z.string(), z.number(), z.null()]))
	.describe("Field/value map matching the org-defined lead schema.");

// ============================================================================
// System-agent backstage tools
// ============================================================================

export const MAX_ANALYTICS_RANGE_DAYS = 184;
export const MAX_KNOWLEDGE_SOURCE_CHARS = 1_048_576;

const AnalyticsDateRangeSchema = z
	.object({
		startDate: SimpleDateString,
		endDate: SimpleDateString,
	})
	.strict()
	.refine(
		({ startDate, endDate }) => {
			const start = Date.parse(`${startDate}T00:00:00.000Z`);
			const end = Date.parse(`${endDate}T00:00:00.000Z`);
			return (
				end >= start && end - start <= MAX_ANALYTICS_RANGE_DAYS * 86_400_000
			);
		},
		{ message: "range exceeds 6 months" }
	);

export const getAnalyticsInputSchema = z
	.object({
		range: AnalyticsDateRangeSchema,
		question: z.string().trim().min(1).max(1000).optional(),
	})
	.strict();

export const getUsageBreakdownInputSchema = z
	.object({ range: AnalyticsDateRangeSchema })
	.strict();

export const getKnowledgeGapsInputSchema = z
	.object({
		status: z.enum(["OPEN", "DRAFTED", "RESOLVED", "DISMISSED"]).optional(),
		limit: z.number().int().min(1).max(100).default(25),
	})
	.strict();

export const renderChartInputSchema = z
	.object({
		chartType: z.enum(["bar", "line", "area", "pie"]),
		title: z.string().max(120),
		description: z.string().max(300).optional(),
		data: z
			.array(z.record(z.string(), z.union([z.string(), z.number()])))
			.min(1)
			.max(500),
		xKey: z.string().min(1),
		series: z
			.array(
				z
					.object({
						dataKey: z.string().min(1),
						label: z.string().max(60),
					})
					.strict()
			)
			.min(1)
			.max(6),
		stacked: z.boolean().optional(),
		yFormat: z.enum(["number", "currency", "percent", "duration"]).optional(),
	})
	.strict();

const KnowledgeTextSchema = z.string().max(MAX_KNOWLEDGE_SOURCE_CHARS);
// `add` is further discriminated by `kind`; using `op` alone here gives Zod
// duplicate discriminator values and fails every add call at runtime.
export const manageKnowledgeSourceInputSchema = z.union([
	z
		.object({
			op: z.literal("add"),
			kind: z.literal("text"),
			title: z.string().trim().min(1).max(200),
			content: KnowledgeTextSchema,
			gapId: z.string().min(1).optional(),
		})
		.strict(),
	z
		.object({
			op: z.literal("add"),
			kind: z.literal("qa"),
			question: z.string().trim().min(1).max(20_000),
			answer: KnowledgeTextSchema,
			gapId: z.string().min(1).optional(),
		})
		.strict(),
	z
		.object({
			op: z.literal("add"),
			kind: z.literal("url"),
			url: z.string().url().max(2048),
		})
		.strict(),
	z
		.object({
			op: z.literal("update"),
			sourceId: z.number().int().positive(),
			title: z.string().trim().min(1).max(200).optional(),
			content: KnowledgeTextSchema.optional(),
			question: z.string().trim().min(1).max(20_000).optional(),
			answer: KnowledgeTextSchema.optional(),
			url: z.string().url().max(2048).optional(),
		})
		.strict()
		.refine(
			(value) =>
				value.title !== undefined ||
				value.content !== undefined ||
				value.question !== undefined ||
				value.answer !== undefined ||
				value.url !== undefined,
			{ message: "update requires at least one changed field" }
		),
	z
		.object({ op: z.literal("delete"), sourceId: z.number().int().positive() })
		.strict(),
	z
		.object({
			op: z.literal("setEnabled"),
			sourceId: z.number().int().positive(),
			enabled: z.boolean(),
		})
		.strict(),
]);

export const ingestUploadedFileInputSchema = z
	.object({ documentId: z.number().int().positive() })
	.strict();
export const triggerRetrainingInputSchema = z.object({}).strict();
export const getAgentSettingsInputSchema = z.object({}).strict();

export const getContactSnapshotInputSchema = z.union([
	z.object({ contactId: z.string().min(1) }).strict(),
	z.object({ phoneE164: PhoneE164StrictSchema }).strict(),
]);

export const manageContactInputSchema = z.discriminatedUnion("op", [
	z
		.object({
			op: z.literal("update"),
			contactId: z.string().min(1),
			name: PersonNameStrictSchema.optional(),
			nickname: z.string().trim().min(1).max(120).nullable().optional(),
			isActive: z.boolean().optional(),
		})
		.strict()
		.refine(
			(value) =>
				value.name !== undefined ||
				value.nickname !== undefined ||
				value.isActive !== undefined,
			{ message: "update requires at least one changed field" }
		),
	z.object({ op: z.literal("delete"), contactId: z.string().min(1) }).strict(),
]);

export const manageBeneficiaryInputSchema = z.discriminatedUnion("op", [
	z
		.object({
			op: z.literal("separate"),
			beneficiaryId: z.string().min(1),
			phoneE164: PhoneE164StrictSchema,
		})
		.strict(),
	z
		.object({
			op: z.literal("mergeIntoParent"),
			beneficiaryId: z.string().min(1),
		})
		.strict(),
	z
		.object({
			op: z.literal("mergeIntoSibling"),
			beneficiaryId: z.string().min(1),
			targetBeneficiaryId: z.string().min(1),
		})
		.strict(),
]);

export const queryConversationsInputSchema = z
	.object({
		range: AnalyticsDateRangeSchema.optional(),
		status: z.enum(["ai_enabled", "handed_off", "open_feedback"]).optional(),
		operatorUserId: z.string().min(1).optional(),
		limit: z.number().int().min(1).max(100).default(25),
		cursor: z.string().optional(),
	})
	.strict();

export const searchConversationsInputSchema = z
	.object({
		query: z.string().trim().min(1).max(1000),
		mode: z.enum(["text", "semantic"]).default("text"),
		range: AnalyticsDateRangeSchema.optional(),
		limit: z.number().int().min(1).max(50).default(20),
	})
	.strict();

export const getConversationMetricsInputSchema = z
	.object({ range: AnalyticsDateRangeSchema })
	.strict();

const WebhookUrlSchema = z.string().url().max(2048);
export const manageAutomationInputSchema = z.discriminatedUnion("op", [
	z
		.object({
			op: z.literal("create"),
			name: z.string().trim().min(1).max(120),
			url: WebhookUrlSchema,
			events: z.array(z.string().min(1)).min(1).max(50),
		})
		.strict(),
	z
		.object({
			op: z.literal("update"),
			automationId: z.string().min(1),
			name: z.string().trim().min(1).max(120).optional(),
			url: WebhookUrlSchema.optional(),
			events: z.array(z.string().min(1)).min(1).max(50).optional(),
		})
		.strict(),
	z
		.object({
			op: z.enum(["enable", "disable", "delete"]),
			automationId: z.string().min(1),
		})
		.strict(),
]);

export const startIntegrationConnectionInputSchema = z
	.object({
		integration: z.enum([
			"whatsapp",
			"telegram",
			"instagram",
			"x",
			"google_calendar",
		]),
	})
	.strict();
export const getChannelStatusInputSchema = z.object({}).strict();
export const generateInsightsReportInputSchema = z
	.object({ days: z.number().int().min(1).max(184).default(7) })
	.strict();

// ============================================================================
// Derived Types - ToolInputSchemas
// ============================================================================

/**
 * Tool input types derived from Zod schemas.
 * This is the single source of truth - types are automatically in sync with schemas.
 */
export interface ToolInputSchemas {
	cancelEvent: z.infer<typeof cancelEventInputSchema>;
	cancelEventRange: z.infer<typeof cancelEventRangeInputSchema>;
	cancelEvents: z.infer<typeof cancelEventsInputSchema>;
	collectLead: z.infer<typeof collectLeadInputSchema>;
	generateInsightsReport: z.infer<typeof generateInsightsReportInputSchema>;
	getAgentSettings: z.infer<typeof getAgentSettingsInputSchema>;
	getAnalytics: z.infer<typeof getAnalyticsInputSchema>;
	getAvailableSlotsBatch: z.infer<typeof getAvailableSlotsBatchInputSchema>;
	getAvailableTimeSlots: z.infer<typeof getAvailableTimeSlotsInputSchema>;
	getChannelStatus: z.infer<typeof getChannelStatusInputSchema>;
	getContactSnapshot: z.infer<typeof getContactSnapshotInputSchema>;
	getConversationMetrics: z.infer<typeof getConversationMetricsInputSchema>;
	// Shared tools
	getCurrentDatetime: z.infer<typeof getCurrentDatetimeInputSchema>;
	getDocumentSchemas: z.infer<typeof getDocumentSchemasInputSchema>;
	// System-agent tools
	getEventSnapshots: z.infer<typeof getEventSnapshotsInputSchema>;
	getKnowledgeGaps: z.infer<typeof getKnowledgeGapsInputSchema>;
	getUsageBreakdown: z.infer<typeof getUsageBreakdownInputSchema>;
	getUserData: z.infer<typeof getUserDataInputSchema>;
	// Assistant tools
	handoffToHuman: z.infer<typeof handoffToHumanInputSchema>;
	ingestUploadedFile: z.infer<typeof ingestUploadedFileInputSchema>;
	joinWaitlist: z.infer<typeof joinWaitlistInputSchema>;
	manageAutomation: z.infer<typeof manageAutomationInputSchema>;
	manageBeneficiary: z.infer<typeof manageBeneficiaryInputSchema>;
	manageContact: z.infer<typeof manageContactInputSchema>;
	manageKnowledgeSource: z.infer<typeof manageKnowledgeSourceInputSchema>;
	modifyEvent: z.infer<typeof modifyEventInputSchema>;
	modifyEventRange: z.infer<typeof modifyEventRangeInputSchema>;
	modifyEvents: z.infer<typeof modifyEventsInputSchema>;
	queryConversations: z.infer<typeof queryConversationsInputSchema>;
	queryStructuredData: z.infer<typeof queryStructuredDataInputSchema>;
	renderChart: z.infer<typeof renderChartInputSchema>;
	requestPhoneNumber: z.infer<typeof requestPhoneNumberInputSchema>;
	saveFeedback: z.infer<typeof saveFeedbackInputSchema>;
	scheduleEvent: z.infer<typeof scheduleEventInputSchema>;
	scheduleEvents: z.infer<typeof scheduleEventsInputSchema>;
	searchAvailableAppointments: z.infer<
		typeof searchAvailableAppointmentsInputSchema
	>;
	searchConversations: z.infer<typeof searchConversationsInputSchema>;
	searchDocs: z.infer<typeof searchDocsInputSchema>;
	sendBusinessLocation: z.infer<typeof sendBusinessLocationInputSchema>;
	sendInteractiveButtons: z.infer<typeof sendInteractiveButtonsInputSchema>;
	sendInteractiveList: z.infer<typeof sendInteractiveListInputSchema>;
	startIntegrationConnection: z.infer<
		typeof startIntegrationConnectionInputSchema
	>;
	summarizeConversation: z.infer<typeof summarizeConversationInputSchema>;
	triggerRetraining: z.infer<typeof triggerRetrainingInputSchema>;
}

/**
 * All tool names as a union type.
 */
export type ToolName = keyof ToolInputSchemas;

/**
 * Date range type for use in services.
 */
export type DateRange = z.infer<typeof DateRangeSchema>;
